import * as React from "react";
import { EMAIL_CODE_LENGTH } from "@taskiq/shared";
import { cn } from "@/lib/cn";

/**
 * The emailed verification code, one box per digit.
 *
 * ── Why this is not six `<input>`s ──────────────────────────────────────────
 *
 * It is **one** input with six boxes drawn behind it. The obvious build — an
 * array of single-character inputs that move focus on keypress — breaks in ways
 * that only show up on real devices: iOS autofill from the SMS/mail heuristic
 * fills the first box and drops the rest, paste lands entirely in whichever box
 * had focus, and a backspace at the start of a box has to be special-cased to
 * step backwards. Every one of those is free here, because the browser is
 * editing a single ordinary text field.
 *
 * The real input is transparent and stretched across the boxes rather than
 * hidden, because `display: none` and `visibility: hidden` both take it out of
 * the accessibility tree and stop autofill offering the code at all. It keeps a
 * caret and a focus ring by way of the boxes reacting to `:focus-within`.
 *
 * `autoComplete="one-time-code"` is what makes iOS and Chrome offer the code
 * from the notification. `inputMode="numeric"` is what gets a digit keypad
 * rather than a full keyboard on a phone.
 */
export function CodeInput({
  value,
  onChange,
  /** Fired when the last digit lands, so the form can submit without a press. */
  onComplete,
  invalid,
  autoFocus,
  disabled,
  /**
   * `md` is the auth screens, where this row is the only thing on the page and
   * is being read off a phone held in the other hand. `sm` is inside a dialog,
   * where six 52px boxes across a 26rem panel are most of what the reader can
   * see and the digits are being copied from an app on the same desk.
   */
  size = "md",
  id = "code",
}: {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  size?: "sm" | "md";
  id?: string;
}) {
  const digits = value.replace(/\D/g, "").slice(0, EMAIL_CODE_LENGTH);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Guarded so that editing a complete code — deleting a digit and retyping it
  // — does not re-fire the submit on every keystroke that lands back on six.
  const firedFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (digits.length < EMAIL_CODE_LENGTH) {
      firedFor.current = null;
      return;
    }
    if (firedFor.current === digits) return;
    firedFor.current = digits;
    onComplete?.(digits);
  }, [digits, onComplete]);

  return (
    <div
      className="code-input"
      // A press anywhere on the row lands in the field, so the boxes behave like
      // the control they are drawn as rather than like decoration beside one.
      onMouseDown={(event) => {
        event.preventDefault();
        inputRef.current?.focus();
      }}
    >
      <div className={cn("code-input__boxes", `code-input__boxes--${size}`)}>
        {Array.from({ length: EMAIL_CODE_LENGTH }, (_, index) => {
          const char = digits[index];
          const isCursor = index === digits.length;

          return (
            <div
              key={index}
              className={cn(
                "code-input__box",
                `code-input__box--${size}`,
                char && "code-input__box--filled",
                invalid && "code-input__box--invalid",
                isCursor && "code-input__box--cursor"
              )}
            >
              {char ?? ""}
            </div>
          );
        })}
      </div>

      <input
        ref={inputRef}
        id={id}
        name="one-time-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        disabled={disabled}
        maxLength={EMAIL_CODE_LENGTH}
        aria-label={`Verification code, ${EMAIL_CODE_LENGTH} digits`}
        aria-invalid={invalid || undefined}
        value={digits}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))}
        className="code-input__field"
      />
    </div>
  );
}
